// rollupData.js — deterministic v2 rollup from the prepared sunburst nodes
// (which carry der_result content in detailedContent: design/execution/read,
// the 12 axis `scores`, plus `headline`, `repair`, `synthesis`). No LLM / no
// backend call.
//
// v2 structure (ChatGPT round 4): the rollup is CATEGORY-first — the weak
// pattern is the header and the units are its nested EVIDENCE, never peers:
//   Pattern → evidence → range map → one move → examples → protect.
// The overview is a cross-grain CONTRAST page that names the ROOT grain to work
// at first ("Start here: Scenes"), not three mini-rollups.

import { unitDisplayLabel, unitSortKey } from './unitLabels';
import { grainTitle, grainPlural, grainSingular } from './grainNouns';

// These three maps were the prose nouns for every script. They are now per-format (grainNouns.js)
// and every builder below takes an optional `script` — ratings.movie — as its LAST parameter, so a
// caller that has not been updated still resolves to the FEATURE nouns and reads exactly as before.

const AXIS_LABEL = {
  contract: 'Contract', pressure: 'Pressure', development: 'Development',
  movement: 'Movement', consequence: 'Consequence', economy: 'Economy',
  assembly: 'Assembly', clarity: 'Clarity', freshness: 'Freshness',
  unpredictability: 'Unpredictability', charge: 'Charge', grip: 'Grip',
};

// Category clause — completes "{Scenes} ___" for the pattern header.
const AXIS_CATEGORY_VERB = {
  contract: "lack a clear thread", pressure: "lack a driving force",
  development: "don't develop", movement: "don't move",
  consequence: "leave no consequence", economy: "carry avoidable waste",
  assembly: "are mis-built", clarity: "are hard to follow",
  freshness: "play it safe", unpredictability: "telegraph their turns",
  charge: "stay emotionally cool", grip: "don't pull forward",
};

// The longer habit clause for the one-sentence subhead.
const AXIS_HABIT = {
  contract: "aren't built around one clear thread",
  pressure: 'drift without a force driving them',
  development: "deliver their beat but don't develop — they restate instead of evolving",
  movement: 'tend to end where they began',
  consequence: 'resolve without leaving a cost behind',
  economy: 'carry avoidable waste',
  assembly: 'are mis-ordered or lose their rhythm',
  clarity: 'are hard to follow',
  freshness: 'play out the way you expect them to',
  unpredictability: 'telegraph their turns',
  charge: 'stay emotionally cool',
  grip: 'let go at the end instead of pulling you forward',
};

const cap = s => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const triScore = dc => (Number(dc.design) || 0) + (Number(dc.execution) || 0) + (Number(dc.read) || 0);
const median = a => { if (!a.length) return null; const s = [...a].sort((x, y) => x - y); const m = Math.floor(s.length / 2); return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2; };

function unitView(n) {
  const dc = n.detailedContent || {};
  const r = dc.repair || {};
	  return {
	    id: n.id,
	    number: dc.number,
	    sortKey: unitSortKey(n),
	    label: unitDisplayLabel(n, 'list') || `${cap(grainSingular(script, n.nodeType, n.nodeType))} ${dc.number}`,
      shortLabel: unitDisplayLabel(n, 'short'),
      actLabel: dc.actLabel || dc.act_label || null,
	    design: dc.design, execution: dc.execution, read: dc.read,
    headline: dc.headline || dc.synthesis || null,
    repairAxis: r.axis || null,
    repairFamily: r.family || null,
    isMiss: !!r.is_miss,
    tri: triScore(dc),
  };
}

// Consecutive low units, length ≥2 → a dead stretch. `lowFn` is GRAIN-RELATIVE
// (a unit dips below its own grain's median) — NOT an absolute <5 — so dead
// stretches surface honestly at every grain, instead of acts/sequences (which
// score on a higher scale) never tripping an absolute floor.
function findRuns(units, lowFn) {
  const runs = [];
  let i = 0;
  const low = lowFn || (u => (Number(u.design) < 5) || (Number(u.read) < 5));
  while (i < units.length) {
    if (low(units[i])) {
      let j = i;
      while (j < units.length && low(units[j])) j++;
      if (j - i >= 2) {
        const seg = units.slice(i, j);
        const worst = seg.reduce((a, b) => (b.tri < a.tri ? b : a), seg[0]);
	        runs.push({ fromNum: seg[0].number, toNum: seg[seg.length - 1].number, fromLabel: seg[0].label, toLabel: seg[seg.length - 1].label, count: seg.length, worstId: worst.id, worstAxis: worst.repairAxis });
      }
      i = j;
    } else i++;
  }
  return runs.sort((a, b) => b.count - a.count);
}

function clusterOf(missUnits, grain) {
	  if (grain === 'act' || !missUnits.length) return null;
	  const tally = {};
	  missUnits.forEach(u => { const c = u.actLabel; if (c) tally[c] = (tally[c] || 0) + 1; });
	  const top = Object.keys(tally).sort((a, b) => tally[b] - tally[a])[0];
	  return top || null;
	}

function example(u) {
  return { id: u.id, label: u.label, number: u.number, axis: u.repairAxis, note: u.headline };
}

function patternFor(grain, units, misses, axis, axisCount, familyTally, lowFn, script) {
  if (!axis) return null;
  const plural = grainPlural(script, grain, grain + 's');
  const onAxis = misses.filter(u => u.repairAxis === axis);
  const family = Object.keys(familyTally[axis] || {}).sort((a, b) => familyTally[axis][b] - familyTally[axis][a])[0];
  const runs = findRuns(units, lowFn);
	  const worstRun = runs[0] ? `${runs[0].fromLabel} to ${runs[0].toLabel}` : null;
  return {
    category: `${grainTitle(script, grain, grain)} ${AXIS_CATEGORY_VERB[axis] || `miss ${AXIS_LABEL[axis]}`}`,
    axis, axisLabel: AXIS_LABEL[axis],
    move: family ? cap(family) + '.' : null,
    evidence: {
      count: `${axisCount[axis]} of ${units.length} ${plural} miss ${AXIS_LABEL[axis]} where it was owed`,
      worstRun,
      cluster: clusterOf(onAxis, grain),
      // Concrete example unit labels (e.g. "Scene 3","Scene 5") so a consumer can
      // list them inline without re-deriving — mirrors RollupPanel's ev.units.
      units: onAxis.map(u => u.label),
    },
    examples: onAxis.slice().sort((a, b) => a.tri - b.tri).slice(0, 3).map(example),
  };
}

/**
 * @returns {object|null} grain rollup, or null when the grain has no units
 */
export function buildRollup(nodes, grain, script = null) {
  const units = (nodes || [])
    .filter(n => n.nodeType === grain && n.detailedContent && n.detailedContent.number != null)
    .map(unitView)
	    .sort((a, b) => (a.sortKey ?? a.number ?? 0) - (b.sortKey ?? b.number ?? 0));
	  if (!units.length) return null;

  const N = units.length;
  const misses = units.filter(u => u.isMiss && u.repairAxis);

  const axisCount = {};
  const familyTally = {};
  misses.forEach(u => {
    axisCount[u.repairAxis] = (axisCount[u.repairAxis] || 0) + 1;
    familyTally[u.repairAxis] = familyTally[u.repairAxis] || {};
    if (u.repairFamily) familyTally[u.repairAxis][u.repairFamily] = (familyTally[u.repairAxis][u.repairFamily] || 0) + 1;
  });
  const rankedAxes = Object.keys(axisCount).sort((a, b) => axisCount[b] - axisCount[a]);
  const dom = rankedAxes[0] || null;
  const secAxis = rankedAxes.find(a => a !== dom && axisCount[a] >= 2) || null;
  const plural = grainPlural(script, grain, grain + 's');

  // GRAIN-RELATIVE weakness: a unit is "low" if it dips below THIS grain's own
  // median (not an absolute <5). Cross-level calibration showed acts/sequences
  // score on a higher scale, so absolute thresholds under-flag them; relative
  // dips are honest at every grain.
  const dMed = median(units.map(u => Number(u.design) || 0)) ?? 5;
  const rMed = median(units.map(u => Number(u.read) || 0)) ?? 5;
  const lowFn = u => (Number(u.design) < dMed - 0.75) || (Number(u.read) < rMed - 0.75);
  const relRuns = findRuns(units, lowFn);
  const dipCount = units.filter(lowFn).length;
  const dispersion = { worstRelRun: relRuns[0] ? relRuns[0].count : 0, dipFrac: N ? dipCount / N : 0, designMedian: dMed };

  // Protect: strongest units, framed as MODELS for the weak ones (writers often
  // "fix" by homogenizing — pull the good scenes down to match).
  const protect = units.slice().sort((a, b) => b.tri - a.tri).slice(0, 2).map(example);

  return {
    grain, grainTitle: grainTitle(script, grain, grain), total: N,
    mainAxis: dom, mainAxisLabel: dom ? AXIS_LABEL[dom] : null,
    subhead: dom ? `Your ${plural} ${AXIS_HABIT[dom] || `keep missing ${AXIS_LABEL[dom]}`}.` : `These ${plural} hold up — no recurring pattern.`,
    pattern: patternFor(grain, units, misses, dom, axisCount, familyTally, lowFn, script),
    secondary: patternFor(grain, units, misses, secAxis, axisCount, familyTally, lowFn, script),
    protect,
    dispersion,
  };
}

/**
 * Overview = cross-grain contrast + the ROOT grain to start at.
 */
export function buildOverview(nodes, script = null) {
  const per = [];
  ['scene', 'sequence', 'act'].forEach(grain => {
    const units = (nodes || []).filter(n => n.nodeType === grain && n.detailedContent && n.detailedContent.number != null);
    if (!units.length) return;
    const r = buildRollup(nodes, grain, script);
    const disp = r.dispersion || { worstRelRun: 0, dipFrac: 0 };

    // Verdict = the SHAPE of the weakness WITHIN this grain (bias-aware — never a
    // cross-grain quality comparison, which the calibration study showed is
    // corrupted by the act/sequence score inflation):
    //   pervasive    — the weakness runs through most units (systemic at this grain)
    //   concentrated — mostly holds, but a real dead stretch (high-leverage fix)
    //   even         — no concentrated weak spot
    let verdict;
    if (disp.dipFrac >= 0.4) verdict = 'pervasive';
    else if (disp.worstRelRun >= 3) verdict = 'concentrated';
    else verdict = 'even';

    const worstRun = r.pattern && r.pattern.evidence.worstRun;
    const oneLine =
      verdict === 'pervasive'    ? `${r.mainAxisLabel || 'The weakness'} runs through most of them.`
      : verdict === 'concentrated' ? `Mostly holds — but ${worstRun || 'a stretch'} dip below the rest.`
      : `Even across — no single stretch stands out.`;
    per.push({ grain, grainTitle: grainTitle(script, grain, grain), verdict, total: units.length, mainAxis: r.mainAxis, mainAxisLabel: r.mainAxisLabel, move: r.pattern && r.pattern.move, oneLine });
  });
  if (!per.length) return null;

  const verdictLabel = { pervasive: 'Throughout', concentrated: 'Soft stretch', even: 'Even' };
  per.forEach(p => { p.verdictLabel = verdictLabel[p.verdict]; });

  // Root grain = the FINEST grain with leverage-worthy work (not 'even'); scene
  // fixes ripple up. (Not "the worst grain" — that'd be the biased comparison.)
  const weak = g => per.find(p => p.grain === g && p.verdict !== 'even');
  const root = weak('scene') || weak('sequence') || weak('act') || per[0];

  const systemic = per.length >= 2 && per.every(p => p.verdict === 'pervasive') && per.every(p => p.mainAxis && p.mainAxis === per[0].mainAxis);
  const allEven = per.every(p => p.verdict === 'even');

  let synthesis;
  if (systemic) {
    synthesis = `The same habit (${root.mainAxisLabel}) runs through every level — this is structural, not a local patch.`;
  } else if (allEven) {
    synthesis = `No concentrated weak spot at any level — this draft is about lifting the ceiling, not patching a floor.`;
  } else {
    synthesis = `Start at the ${grainSingular(script, root.grain, root.grain)} level${root.verdict === 'concentrated' ? ' — its weakness is concentrated, so a few fixes go a long way' : ''}; ${root.mainAxisLabel || 'that'} is the lever, and scene-level fixes ripple up.`;
  }
  const startHere = `Start here: ${root.grainTitle}.`;
  const primaryMove = root.move || null;

  return {
    synthesis, systemic, rootGrain: root.grain, rootLabel: root.grainTitle, startHere, primaryMove, grains: per,
    caveat: 'Each level is scored on its own scale — compare units within a level, not levels against each other.',
  };
}

/** Which grains actually have units (to show/disable the tabs). */
export function availableGrains(nodes) {
  const out = {};
  ['scene', 'sequence', 'act'].forEach(g => {
    out[g] = (nodes || []).some(n => n.nodeType === g && n.detailedContent && n.detailedContent.number != null);
  });
  return out;
}

// ─────────────────────── Per-axis, per-grain summary (Tier 2) ───────────────────────
// Deterministic one-liner for ANY axis at ANY grain, from the raw 12-axis `scores`
// already on each node — so the Shape-tab radar hover has grounded content on every
// spoke, not just the 1–2 axes the miss-pattern flags. No LLM / no backend call.
const AXIS_NOT_OWED = new Set(['not_owed', 'na', 'not_relevant', 'not_applicable']);
// Mirror of prepareChartData's cellOwed, kept local to stay dependency-free.
function axisCellOwed(c) { return !!(c && c.score != null && !AXIS_NOT_OWED.has(c.status)); }
const GRAIN_NODETYPE = { scene: 'scene', sequence: 'sequence', act: 'act', script: 'movie' };

export function buildAxisCell(nodes, grain, axis, script = null) {
  const nt = GRAIN_NODETYPE[grain] || grain;
  const units = (nodes || []).filter(n => n.nodeType === nt && n.detailedContent && n.detailedContent.number != null);
  const rows = units.map(u => {
    const c = (u.detailedContent.scores || {})[axis];
    return c ? { c, num: u.detailedContent.number, score: parseFloat(c.score) } : null;
  }).filter(Boolean);
  const owed = rows.filter(r => axisCellOwed(r.c) && !Number.isNaN(r.score));
  const totalN = units.length;
  if (!owed.length) return { kind: 'not_owed', totalN };
  const label = (r) => (grain === 'script')
    ? 'the whole script'
    : `${cap(grainSingular(script, grain, grain))} ${r.num}`;
  const mean = owed.reduce((a, r) => a + r.score, 0) / owed.length;
  const strongest = owed.reduce((a, r) => (r.score > a.score ? r : a), owed[0]);
  const weakest = owed.reduce((a, r) => (r.score < a.score ? r : a), owed[0]);
  const suggestion = (weakest.c.improvementSuggestions || []).find(Boolean) || null;
  return {
    kind: 'ok',
    owedN: owed.length,
    totalN,
    grainPlural: grainPlural(script, grain, grain + 's'),
    mean: Math.round(mean * 10) / 10,
    strongest: { label: label(strongest), score: strongest.score },
    weakest: { label: label(weakest), score: weakest.score },
    suggestion,
    single: totalN <= 1 || owed.length <= 1,
  };
}
